import { motion } from "framer-motion";
import { IMAGES } from "../lib/images";
import { ArrowDown, MapPin } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="hero" className="relative overflow-hidden pt-20 md:pt-0">
      <h1 className="sr-only">Macarthur Wine Society</h1>

      {/* Banner image at natural aspect ratio */}
      <div className="relative w-full">
        {/* top gradient for nav legibility */}
        <div className="absolute inset-x-0 top-0 z-10 h-28 bg-gradient-to-b from-wine-bg/70 to-transparent" aria-hidden="true" />
        <motion.img
          src={IMAGES.heroBanner}
          alt="Macarthur Wine Society"
          initial={{ scale: 1.04, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="block w-full h-auto max-h-[82vh] object-cover object-center"
        />
        {/* bottom fade into page */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-wine-bg via-wine-bg/50 to-transparent" aria-hidden="true" />
      </div>

      {/* Copy beneath the banner */}
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12 pb-20 -mt-10 md:-mt-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mb-8 flex items-center gap-3 text-cream"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="text-xs md:text-sm uppercase tracking-[0.28em]">Macarthur · New South Wales</span>
        </motion.div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45 }}
            className="max-w-xl font-display text-2xl md:text-3xl text-cream leading-snug"
          >
            For those who love a good glass{" "}
            <span className="italic text-terracotta">&amp; better company.</span>
            <span className="mt-4 block font-sans text-base md:text-lg text-smoke leading-relaxed">
              A relaxed, social wine society for local wine lovers of all ages. Tastings, winery
              tours and good evenings out — no expert knowledge required, just curiosity
              and good humour.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-join-btn"
              onClick={() => scrollTo("join")}
              className="rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-wine-bg transition-all duration-300 hover:-translate-y-1 hover:bg-cream"
            >
              Join the Society
            </button>
            <button
              data-testid="hero-events-btn"
              onClick={() => scrollTo("events")}
              className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:border-gold hover:text-gold"
            >
              See the first gathering
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-14 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-smoke"
        >
          <ArrowDown className="h-4 w-4 animate-bounce text-gold" />
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> First stop — Gledswood Homestead &amp; Winery
          </span>
        </motion.div>
      </div>
    </section>
  );
}
